import { Injectable } from "@angular/core";

import { AppState } from "../../core/app-state";

import { HttpClient } from "@angular/common/http";
import { first, firstValueFrom } from "rxjs";
import { RestClient } from "../../core/services/rest-client";
import {
  VolunteerTaskRecord,
  VolunteerTaskViewRecord,
} from "../models/volunteer-task-record";
import { VolunteerTaskService } from "./volunteer-task.service";
import { VolunteerTaskPointViewRecord } from "../models/volunteer-task-point-record";
import { BsModalService } from "ngx-bootstrap/modal";
import { ConfirmModalComponent } from "../../shared/components/confirm-modal/confirm-modal.component";
import { activity_status } from "../../core/models";
import { FesRutTestPointTestParams } from "../models";

@Injectable({ providedIn: "root" })
export class VolunteerTaskViewService extends RestClient<VolunteerTaskViewRecord> {
  constructor(
    public appState: AppState,
    http: HttpClient,
    public volunteerTaskService: VolunteerTaskService,
    public bsModalService: BsModalService
  ) {
    super(http, "volunteerTaskView", appState);
  }

  async saveWithoutPoints( task: VolunteerTaskRecord ) {
    return await this.saveWithPoints(task,null);
  }

  async saveWithPoints(
    task: VolunteerTaskRecord,
    points: VolunteerTaskPointViewRecord[],
    customTestParams?: FesRutTestPointTestParams
  ) {
    const resp: any = await firstValueFrom(
      this.http.put<{
        task: VolunteerTaskViewRecord;
        points: VolunteerTaskPointViewRecord[];
      }>(
        `${this.urlPath}/save`,
        { task, points, customTestParams },
        {
          observe: "response",
        }
      )
    );
    const result: {
      task: VolunteerTaskViewRecord;
      points: VolunteerTaskPointViewRecord[];
    } = resp.body;
    //console.log(JSON.stringify(result));
    return result;
  }

  async patch(fields: Partial<VolunteerTaskViewRecord>) {
    return await this.volunteerTaskService.patch(fields);
  }

  async deleteTaskById(taskId:number) {
    return await this.volunteerTaskService.deleteTaskById(taskId);
  }

  async promptedDeleteTask(task: VolunteerTaskViewRecord) {
    let deletedTask: VolunteerTaskRecord = null;
    let deleteConfirmed = false;
    const initialState = {
      onYes: async () => deleteConfirmed = true,
      title: `Confirm Deletion`,
      question: `<div class="m-2 font-italic text-danger">
            <i class='text-warning fa fa-exclamation-triangle mr-1'></i>
            Delete ${task?.type} for user ${task?.volunteerName}?</div>
            <div class='font-italic text-secondary small'>Includes ${task?.taskPoints?.length||0} task points</div>`,
      focusOnNo: true,
      yesStyle: "btn btn-danger",
    };
    const modalRef = this.bsModalService.show(ConfirmModalComponent, {
      ignoreBackdropClick: true,
      id: 20221116,
      initialState,
    });
    await firstValueFrom(modalRef.onHidden.pipe(first()));
    if ( deleteConfirmed ) {
      try {
        deletedTask = await this.deleteTaskById(task.id);
        this.appState.notifySuccess(
          `Deleted task (building: ${task.buildingId}, volunteer: ${task.volunteerName})`,
          this,
          2
        );
      } catch (error: any) {
        const errorMsg = this.extractErrorMsg(error);
        console.error(error);
        if ( errorMsg.indexOf('constraint') != -1 ) {
          return await this.promptedArchiveTask(task,'Task has existing points or call data and cannot be deleted');
        } else {
          this.publishError(error, this);
          this.appState.notifyError(errorMsg, this, 2);
        }
      }
    }
    return deletedTask;
  }

  async promptedArchiveTask(task: VolunteerTaskViewRecord, detailMsg: string = null) {
    let archivedTask: VolunteerTaskRecord = null;
    let archiveConfirmed = false;
    const initialState = {
      onYes: async () => archiveConfirmed = true,
      title: `Confirm Archive Status`,
      question: `<div *ngIf="detailMsg">${detailMsg}</div><div class="m-2 font-italic text-danger">
            <i class='text-warning fa fa-exclamation-triangle mr-1'></i>
            Archive ${task?.type} for user ${task?.volunteerName}?</div>            
            <div class='font-italic text-secondary small'>Includes ${task?.taskPoints?.length||0} task points</div>`,
      focusOnNo: true,
      yesStyle: "btn btn-danger",
    };
    const modalRef = this.bsModalService.show(ConfirmModalComponent, {
      ignoreBackdropClick: true,
      id: 20230707,
      initialState,
    });
    await firstValueFrom(modalRef.onHidden.pipe(first()));
    if ( archiveConfirmed ) {
      try {
        archivedTask = await this.patch({
          id: task.id,
          status: activity_status.Id.ARCHIVED,
        });
        this.appState.notifySuccess(
          `Archived task (building: ${task.buildingId}, volunteer: ${task.volunteerName})`,
          this,
          2
        );
      } catch (error: any) {
        const errorMsg = this.extractErrorMsg(error);
        this.publishError(error, this);
        this.appState.notifyError(errorMsg, this, 2);
      }
    }
    return archivedTask;
  }

}
